// Vendor Return detail — full lifecycle actions with mixed outcomes.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errorToMessage } from "@/lib/apiClient";

const STATUS_CHIP = {
  DRAFT:         "bg-slate-100 text-slate-700 border-slate-200",
  SENT:          "bg-amber-100 text-amber-800 border-amber-200",
  RECEIVED_BACK: "bg-sky-100   text-sky-800   border-sky-200",
  CLOSED:        "bg-emerald-100 text-emerald-800 border-emerald-200",
  CANCELLED:     "bg-rose-100  text-rose-800  border-rose-200",
};
const OUTCOME_CHIP = {
  REPAIRED:           "bg-emerald-100 text-emerald-800 border-emerald-200",
  REPLACED:           "bg-blue-100    text-blue-800    border-blue-200",
  CREDIT_NOTE:        "bg-indigo-100  text-indigo-800  border-indigo-200",
  WARRANTY_REJECTED:  "bg-rose-100    text-rose-800    border-rose-200",
  SCRAPPED_BY_VENDOR: "bg-slate-200   text-slate-700   border-slate-300",
};

export default function VendorReturnDetailPage() {
  const { vr_id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openItem, setOpenItem] = useState(null); // id currently being closed

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/vendor-returns/${vr_id}`);
      setData(data);
    } catch (e) { setError(errorToMessage(e, "Could not load Vendor Return")); }
    finally { setLoading(false); }
  }, [vr_id]);

  useEffect(() => { load(); }, [load]);

  const status = data?.header?.status;
  const openItems = useMemo(
    () => (data?.items || []).filter((it) => !it.outcome),
    [data]
  );

  const dispatch = async () => {
    setBusy(true);
    try {
      const courier = window.prompt("Courier name (optional):", data?.header?.courier_name || "");
      const tracking = window.prompt("Tracking number (optional):", data?.header?.tracking_no || "");
      await api.post(`/vendor-returns/${vr_id}/dispatch`, {
        courier_name: courier || null, tracking_no: tracking || null,
      });
      await load();
    } catch (e) { setError(errorToMessage(e, "Dispatch failed")); }
    finally { setBusy(false); }
  };

  const receiveBack = async () => {
    if (!window.confirm("Mark this Vendor Return as RECEIVED_BACK from vendor?")) return;
    setBusy(true);
    try {
      await api.post(`/vendor-returns/${vr_id}/mark-received-back`);
      await load();
    } catch (e) { setError(errorToMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    try {
      const { data: blob } = await api.get(
        `/vendor-returns/${vr_id}/challan.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { setError(errorToMessage(e, "PDF failed")); }
  };

  if (loading) return <section className="p-6 text-sm text-slate-500">Loading…</section>;
  if (!data) return <section className="p-6 text-sm text-rose-600">{error || "Not found"}</section>;

  const h = data.header;

  return (
    <section className="p-6 space-y-4" data-testid="vr-detail-page">
      {/* Header */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Vendor Return</div>
            <div className="text-2xl font-bold font-mono text-slate-900" data-testid="vr-number">
              {h.return_no}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {h.vendor_name} · {h.return_date} · <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_CHIP[status] || ""}`}
                data-testid="vr-status">{status}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadPDF}
                    data-testid="vr-pdf-btn"
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
              📄 Download Challan PDF
            </button>
            {status === "DRAFT" && (
              <button onClick={dispatch} disabled={busy}
                      data-testid="vr-dispatch-btn"
                      className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700">
                Dispatch to Vendor
              </button>
            )}
            {status === "SENT" && (
              <button onClick={receiveBack} disabled={busy}
                      data-testid="vr-receive-back-btn"
                      className="rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700">
                Mark Received Back
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-slate-600 md:grid-cols-4">
          <div><span className="text-slate-500">Courier:</span> {h.courier_name || "—"}</div>
          <div><span className="text-slate-500">Tracking:</span> {h.tracking_no || "—"}</div>
          <div><span className="text-slate-500">Dispatched:</span> {h.dispatched_at?.slice(0, 19).replace("T", " ") || "—"}</div>
          <div><span className="text-slate-500">Received Back:</span> {h.received_back_at?.slice(0, 19).replace("T", " ") || "—"}</div>
        </div>
        {h.remarks && <div className="mt-2 text-xs italic text-slate-600">{h.remarks}</div>}
      </div>

      {error && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      {/* Items */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
          <div className="text-sm font-semibold text-slate-800">Items ({data.items.length})</div>
          <div className="text-xs text-slate-500">
            Open: {openItems.length} · Closed: {data.items.length - openItems.length}
          </div>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Passport</th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Passport Status</th>
              <th className="px-3 py-2 text-left">Outcome</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`vr-item-row-${it.passport_uid}`}>
                <td className="px-3 py-2">
                  <Link to={`/admin/passports/${it.passport_id}`}
                        className="font-mono text-blue-700 font-semibold hover:underline">
                    {it.passport_uid}
                  </Link>
                  {it.item_type && it.item_type !== "laptop" && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-1.5 py-0 text-[9px] font-semibold uppercase text-indigo-800">
                      {it.item_type}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{it.item_description || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {it.return_reason?.replace(/_/g, " ")}
                  {it.reason_notes && <div className="text-[10px] text-slate-500 italic">{it.reason_notes}</div>}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{it.passport_status}</td>
                <td className="px-3 py-2">
                  {it.outcome ? (
                    <>
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${OUTCOME_CHIP[it.outcome] || ""}`}>
                        {it.outcome.replace(/_/g, " ")}
                      </span>
                      {it.replacement_uid && (
                        <div className="mt-1 text-[10px] font-mono text-blue-700">→ {it.replacement_uid}</div>
                      )}
                      {it.credit_amount && <div className="mt-1 text-[10px] text-indigo-700">₹{it.credit_amount}</div>}
                    </>
                  ) : (
                    <span className="text-xs italic text-slate-400">Pending</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {!it.outcome && (status === "SENT" || status === "RECEIVED_BACK") && (
                    <button onClick={() => setOpenItem(it)}
                            data-testid={`vr-close-btn-${it.passport_uid}`}
                            className="rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-800">
                      Close Item
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openItem && (
        <CloseItemModal item={openItem} vrId={vr_id}
                        onClose={() => setOpenItem(null)}
                        onDone={async () => { setOpenItem(null); await load(); }} />
      )}
    </section>
  );
}


function CloseItemModal({ item, vrId, onClose, onDone }) {
  const [outcome, setOutcome] = useState("REPAIRED");
  const [notes, setNotes] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [replSerial, setReplSerial] = useState("");
  const [receiveDate, setReceiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (outcome === "REPLACED") {
        await api.post(`/vendor-returns/${vrId}/items/${item.id}/receive-replacement`, {
          receive_date: receiveDate,
          manufacturer_serial: replSerial || null,
          remarks: notes || null,
        });
      } else {
        await api.post(`/vendor-returns/${vrId}/items/${item.id}/close`, {
          outcome, outcome_notes: notes || null,
          credit_amount: outcome === "CREDIT_NOTE" && creditAmount ? Number(creditAmount) : null,
        });
      }
      await onDone();
    } catch (e) { setError(errorToMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40"
         onClick={onClose} data-testid="vr-close-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <div className="text-lg font-semibold text-slate-900">Close Item</div>
        <div className="mt-1 text-xs text-slate-500 font-mono">{item.passport_uid} · {item.item_type}</div>

        <label className="mt-3 block text-xs font-medium text-slate-700">Outcome
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                  data-testid="vr-close-outcome">
            <option value="REPAIRED">Repaired (same unit returned)</option>
            <option value="REPLACED">Replaced (new unit — generates new Passport ID)</option>
            <option value="CREDIT_NOTE">Credit Note received</option>
            <option value="WARRANTY_REJECTED">Warranty Rejected</option>
            <option value="SCRAPPED_BY_VENDOR">Scrapped by Vendor</option>
          </select>
        </label>

        {outcome === "REPLACED" && (
          <>
            <label className="mt-3 block text-xs font-medium text-slate-700">Physical Receive Date
              <input type="date" value={receiveDate} onChange={(e) => setReceiveDate(e.target.value)}
                     className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                     data-testid="vr-repl-date" />
              <div className="mt-1 text-[10px] text-slate-500">Passport ID uses this date (RP{receiveDate.replace(/-/g, "").slice(2)}NNNN).</div>
            </label>
            <label className="mt-3 block text-xs font-medium text-slate-700">Manufacturer Serial (optional)
              <input value={replSerial} onChange={(e) => setReplSerial(e.target.value)}
                     placeholder="Scan or type the new unit's serial"
                     className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                     data-testid="vr-repl-serial" />
            </label>
          </>
        )}
        {outcome === "CREDIT_NOTE" && (
          <label className="mt-3 block text-xs font-medium text-slate-700">Credit Amount (₹)
            <input type="number" step="0.01" min="0" value={creditAmount}
                   onChange={(e) => setCreditAmount(e.target.value)}
                   className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                   data-testid="vr-credit-amount" />
          </label>
        )}
        <label className="mt-3 block text-xs font-medium text-slate-700">Notes (optional)
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                    data-testid="vr-close-notes" />
        </label>

        {error && <div className="mt-2 text-xs text-rose-700">{error}</div>}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={submit} disabled={busy}
                  data-testid="vr-close-confirm"
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
            {busy ? "Working…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
